import { ITEMS } from "./data/items.ts";
import { bird } from "./data/story.ts";
import { STAT_KEYS, type Hero, type Slot } from "./types.ts";

const KEY = "coop-detat/save/v1";
const known = new Set(ITEMS.map((i) => i.id));

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Loads the saved hero, dropping anything that no longer matches the current game data. */
export function loadHero(): Hero | null {
  let raw: unknown;
  try {
    raw = JSON.parse(localStorage.getItem(KEY) ?? "null");
  } catch {
    return null;
  }
  if (!isRecord(raw) || typeof raw.name !== "string" || !isRecord(raw.base) || !isRecord(raw.look)) return null;
  const base = raw.base;
  if (!STAT_KEYS.every((k) => typeof base[k] === "number")) return null;
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const inventory = strings(raw.inventory).filter((id) => known.has(id));
  const gear: Hero["gear"] = {};
  if (isRecord(raw.gear)) {
    for (const [slot, id] of Object.entries(raw.gear)) {
      if (typeof id === "string" && known.has(id) && inventory.includes(id)) gear[slot as Slot] = id;
    }
  }
  return {
    name: raw.name.slice(0, 22) || "Chicken",
    look: { ...bird(), ...raw.look, kind: "bird" },
    base: Object.fromEntries(STAT_KEYS.map((k) => [k, num(base[k], 3)])) as Hero["base"],
    level: num(raw.level, 1),
    xp: num(raw.xp, 0),
    points: num(raw.points, 0),
    gold: num(raw.gold, 0),
    inventory,
    gear,
    specials: strings(raw.specials).filter((s): s is Hero["specials"][number] => ["egg", "roar", "frenzy", "wind"].includes(s)),
    progress: num(raw.progress, 0),
    wins: num(raw.wins, 0),
    losses: num(raw.losses, 0),
    seenScenes: strings(raw.seenScenes),
    finished: raw.finished === true,
  };
}

export function saveHero(hero: Hero): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(hero));
  } catch {
    // Storage full or disabled (private mode): the run continues unsaved.
  }
}

export function clearSave(): void {
  localStorage.removeItem(KEY);
}
